import React from 'react';
import { Platform, SectionListData, SectionListRenderItem, StyleSheet, View } from 'react-native';

import { handleAuthError } from '@auth/AuthErrorHandler';
import { deleteUser, signOut } from '@auth/ManageUser';
import { ConfirmDeleteDialog } from '@components/Dialog';
import Divider from '@components/Divider';
import { clearImageCache } from '@components/Image';
import { PressableOpacity } from '@components/Pressable';
import ScreenContainer from '@components/ScreenContainer';
import SectionList from '@components/SectionList';
import { Caption, Subheading, Title } from '@components/Typography';
import { ThemeToggle, useTheme } from '@context/ThemeContext';
import { alert } from '@helpers/Alert';
import { getVersionText, isDev } from '@helpers/Constants';
import {
  openContactUs,
  openDataPolicy,
  openPrivacyPolicy,
  openTermsPolicy,
} from '@helpers/Linking';
import { clearCache as clearLocalStorageCache } from '@helpers/LocalStorage';
import { getAuthenticatedUser } from '@helpers/firebase/Auth';
import { SettingsScreenProps } from '@navigation/Types';

export const DELETE_ACCOUNT_CONFIRMATION_TITLE = 'Delete Account';
export const DELETE_ACCOUNT_CONFIRMATION_MESSAGE =
  "This will permanently delete your Joinstar account and can't be undone.";
export const DELETE_ACCOUNT_SUCCESS_TITLE = 'Account Deleted';
export const DELETE_ACCOUNT_SUCCESS_MESSAGE =
  'Your account has been deleted. We are removing account data associated with your profile.';

export const SETTINGS_TEXT = {
  sections: {
    account: 'Account',
    development: 'Development',
    helpAndPolicies: 'Help and Policies',
  },
  items: {
    clearCache: 'Clear Cache',
    contactUs: 'Contact Us',
    deleteAccount: 'Delete Account',
    privacyPolicy: 'Privacy Policy',
    requestData: 'Request Your Data',
    signOut: 'Sign Out',
    switchToDarkTheme: 'Switch to Dark Theme',
    switchToLightTheme: 'Switch to Light Theme',
    termsOfService: 'Terms of Service',
  },
} as const;

type SettingsItem = {
  onPress: () => Promise<void> | void;
  text: string;
};

type SettingsSection = {
  data: SettingsItem[];
  title: string;
};

export default function SettingsScreen({ navigation }: SettingsScreenProps) {
  const [showDeleteAccountDialog, setShowDeleteAccountDialog] = React.useState(false);
  const { theme, setTheme } = useTheme();

  const renderItem: SectionListRenderItem<SettingsItem, SettingsSection> = ({ item }) => (
    <PressableOpacity onPress={item.onPress}>
      <Subheading style={styles.text}>{item.text}</Subheading>
    </PressableOpacity>
  );

  const renderSectionHeader = ({
    section,
  }: {
    section: SectionListData<SettingsItem, SettingsSection>;
  }) => (
    <View>
      <Title style={styles.text}>{section.title}</Title>
      <Divider />
    </View>
  );

  const clearCache = () => {
    if (Platform.OS !== 'web') {
      clearImageCache();
    }
    clearLocalStorageCache();
  };

  const onSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      handleAuthError(error);
    }
  };

  const onDeleteAccount = async () => {
    setShowDeleteAccountDialog(false);
    try {
      await deleteUser(getAuthenticatedUser());
      alert(DELETE_ACCOUNT_SUCCESS_TITLE, DELETE_ACCOUNT_SUCCESS_MESSAGE);
    } catch (error) {
      handleAuthError(error);
    }
  };

  const onThemeToggle = () => {
    return theme === ThemeToggle.DARK ? setTheme(ThemeToggle.LIGHT) : setTheme(ThemeToggle.DARK);
  };

  const sections: SettingsSection[] = [
    {
      data: [
        { text: SETTINGS_TEXT.items.contactUs, onPress: openContactUs },
        { text: SETTINGS_TEXT.items.termsOfService, onPress: openTermsPolicy },
        { text: SETTINGS_TEXT.items.privacyPolicy, onPress: openPrivacyPolicy },
        { text: SETTINGS_TEXT.items.requestData, onPress: openDataPolicy },
      ],
      title: SETTINGS_TEXT.sections.helpAndPolicies,
    },
    ...(isDev()
      ? [
          {
            data: [
              {
                text:
                  theme === ThemeToggle.DARK
                    ? SETTINGS_TEXT.items.switchToLightTheme
                    : SETTINGS_TEXT.items.switchToDarkTheme,
                onPress: onThemeToggle,
              },
              { text: SETTINGS_TEXT.items.clearCache, onPress: clearCache },
            ],
            title: SETTINGS_TEXT.sections.development,
          },
        ]
      : []),
    {
      data: [
        {
          text: SETTINGS_TEXT.items.signOut,
          onPress: onSignOut,
        },
        {
          text: SETTINGS_TEXT.items.deleteAccount,
          onPress: () => setShowDeleteAccountDialog(true),
        },
      ],
      title: SETTINGS_TEXT.sections.account,
    },
  ];

  return (
    <ScreenContainer>
      <SectionList
        initialNumToRender={25}
        sections={sections}
        renderItem={renderItem}
        renderSectionHeader={renderSectionHeader}
        ListFooterComponent={<Caption style={styles.versionText}>{getVersionText()}</Caption>}
        style={styles.list}
        contentContainerStyle={styles.listContainer}
      />
      <ConfirmDeleteDialog
        visible={showDeleteAccountDialog}
        onCancel={() => setShowDeleteAccountDialog(false)}
        onDelete={onDeleteAccount}
        title={DELETE_ACCOUNT_CONFIRMATION_TITLE}
        message={DELETE_ACCOUNT_CONFIRMATION_MESSAGE}
      />
    </ScreenContainer>
  );
}

const styles = StyleSheet.create({
  list: {
    width: '100%',
  },
  listContainer: {
    marginTop: 5,
  },
  text: {
    padding: 10,
    paddingHorizontal: 20,
  },
  versionText: {
    textAlign: 'center',
  },
});
